import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { TFuncKey, Trans, useTranslation } from 'react-i18next'
import { finalize, from, switchMap } from 'rxjs'
import * as Yup from 'yup'

import { LoadingButton } from '@mui/lab'
import { FormGroup, Stack, Typography } from '@mui/material'
import { AlertMessage } from '@procom-labs/atoms'
import { RecaptchaActions, useSubscriptionRef } from '@procom-labs/common'
import {
  DigitalCodeInput,
  TextHookField,
  useAlert,
} from '@procom-labs/molecules'

import { RenderCheckbox } from '@auth-portal/components/password-validation'
import { useAuthService, useRecaptcha } from '@auth-portal/hooks'
import { ValidateSignInFormValues, ValidationType } from '@auth-portal/types'
import {
  DefaultPasswordValidationState,
  passwordValidations,
} from '@auth-portal/util'

import { CommonEndAdornment } from './common-end-adornment'

export const PasswordCreateScreen: FC<{
  isLoading: boolean
}> = ({ isLoading }) => {
  const { addAlert } = useAlert()
  const authService = useAuthService()
  const { getRecaptchaToken } = useRecaptcha()
  const { t, i18n } = useTranslation('main')

  const resendOtpSubscriptionRef = useSubscriptionRef()
  const verifyCodeSubscriptionRef = useSubscriptionRef()

  const [showPassword, setShowPassword] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isOtpValid, setIsOtpValid] = useState(false)
  const [passwordValidation, setPasswordValidation] = useState<
    ValidationType[]
  >(DefaultPasswordValidationState)

  const isValidPasswords = useMemo(
    () => passwordValidation.every((validation) => validation.valid),
    [passwordValidation]
  )

  const { control, setValue } = useFormContext<ValidateSignInFormValues>()

  const email = useWatch({ control, name: 'email' })
  const password = useWatch({ control, name: 'password' })
  const confirmPassword = useWatch({ control, name: 'confirmPassword' })

  const handleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  const handleResendOtp = useCallback(() => {
    resendOtpSubscriptionRef.current = from(
      getRecaptchaToken(RecaptchaActions.RequestResetPassword)
    )
      .pipe(
        switchMap((recaptcha) =>
          authService.generateSignInOtp(
            decodeURIComponent(email),
            recaptcha,
            i18n.language
          )
        ),
        finalize(() => setIsVerifying(false))
      )
      .subscribe({
        next: () => {
          addAlert({
            severity: 'info',
            message: `${t(
              'form.alerts.codeExpired'
            )} New code has been sent to your email`,
          })
        },
      })
  }, [
    t,
    email,
    addAlert,
    i18n.language,
    getRecaptchaToken,
    resendOtpSubscriptionRef,
  ])

  const handleVerifyCode = useCallback(
    (code: string) => {
      setIsVerifying(true)
      setIsOtpValid(false)
      setValue('otp', code)
      verifyCodeSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.RequestResetPassword)
      )
        .pipe(
          switchMap((token) =>
            authService.validateOtpSignIn(email, code, token)
          ),
          finalize(() => setIsVerifying(false))
        )
        .subscribe({
          next: (response) => {
            if (response.isCodeValid) {
              setIsOtpValid(true)
            } else if (response.hasCodeExpired) {
              handleResendOtp()
            } else if (response.hasReachedMaxAttempts) {
              addAlert({
                severity: 'error',
                message: t('form.alerts.maxAttempts', {
                  hour: 3,
                }),
              })
            } else {
              addAlert({
                severity: 'error',
                message: t('form.alerts.invalidCode'),
              })
            }
          },
          error: (error) => {
            setIsOtpValid(false)
            addAlert({
              severity: 'error',
              message: error.message,
            })
          },
        })
    },
    [
      t,
      email,
      addAlert,
      setValue,
      getRecaptchaToken,
      handleResendOtp,
      verifyCodeSubscriptionRef,
    ]
  )

  const validatePassword = useCallback(() => {
    if (!password) {
      setPasswordValidation(
        passwordValidation.map((item) => ({ ...item, valid: false }))
      )
      return
    }

    const validationMap: Partial<
      Record<TFuncKey, (password: string) => Promise<boolean>>
    > = {
      [passwordValidations.Min6Characters]: (pass: string) =>
        Yup.string().min(6).isValid(pass),
      [passwordValidations.Min1number]: (pass: string) =>
        Yup.string().matches(/\d+/).isValid(pass),
      [passwordValidations.Min1CapitalLetter]: (pass: string) =>
        Yup.string()
          .matches(/[A-Z]+/)
          .isValid(pass),
      [passwordValidations.Min1LowerCaseLetter]: (pass: string) =>
        Yup.string()
          .matches(/[a-z]+/)
          .isValid(pass),
      [passwordValidations.PasswordMatch]: (pass: string) =>
        Yup.string().oneOf([confirmPassword]).isValid(pass),
    }

    Promise.all(
      passwordValidation.map(async (item) => {
        const validationFunction = item.transKey
          ? validationMap[item.transKey]
          : () => Promise.resolve(false)

        const result = validationFunction
          ? await validationFunction(password)
          : false

        return {
          ...item,
          valid: result,
        }
      })
    ).then((newValidation) => {
      setPasswordValidation(newValidation)
    })
  }, [password, confirmPassword, passwordValidation])

  useEffect(() => {
    validatePassword()
  }, [password, confirmPassword])

  return (
    <Stack direction="column" rowGap={1} marginX={4}>
      <Typography
        component="h1"
        variant="h30Bold800"
        align="center"
        lineHeight={1.5}
        mb={1}
      >
        {t('organisms.mfaLoginCodeVerification.heading')}
      </Typography>

      <TextHookField
        control={control}
        name="email"
        label={t('form.email.label')}
        required
        disabled
        defaultValue={email}
        customLabel={false}
      />

      <TextHookField
        name="password"
        control={control}
        required
        label={t('form.changePassword.label')}
        type={showPassword ? 'text' : 'password'}
        customLabel={false}
        InputLabelProps={{
          shrink: false,
        }}
        InputProps={{
          endAdornment: (
            <CommonEndAdornment
              showPassword={showPassword}
              handleShowPassword={handleShowPassword}
            />
          ),
        }}
      />

      <FormGroup sx={{ mb: '12px', width: '100%' }}>
        {passwordValidation.map((validation) =>
          validation.transKey !== passwordValidations.PasswordMatch ? (
            <RenderCheckbox key={validation.transKey} validation={validation} />
          ) : (
            ''
          )
        )}
      </FormGroup>

      <TextHookField
        control={control}
        name="confirmPassword"
        required
        label={t('form.changePassword.label3')}
        error={false}
        type={showPassword ? 'text' : 'password'}
        customLabel={false}
        InputLabelProps={{
          shrink: false,
        }}
        InputProps={{
          endAdornment: (
            <CommonEndAdornment
              showPassword={showPassword}
              handleShowPassword={handleShowPassword}
            />
          ),
        }}
      />

      <FormGroup sx={{ mb: '24px', width: '100%' }}>
        {passwordValidation.map((validation) =>
          validation.transKey === passwordValidations.PasswordMatch ? (
            <RenderCheckbox key={validation.transKey} validation={validation} />
          ) : (
            ''
          )
        )}
      </FormGroup>

      <AlertMessage severity="info" sx={{ textAlign: 'left' }}>
        <Typography>
          <Trans i18nKey="common.alert.verifyDigitalCode" values={{ email }} />
        </Typography>
      </AlertMessage>

      <Typography sx={{ textAlign: 'center' }}>
        {t('form.createAccount.headings.enterCodeToVerify')}
      </Typography>
      <DigitalCodeInput
        isVerifying={isVerifying}
        handleVerifyCode={handleVerifyCode}
      />

      <LoadingButton
        size="large"
        type="submit"
        color="secondary"
        variant="contained"
        fullWidth
        disabled={!isOtpValid || !isValidPasswords}
        loading={isLoading || isVerifying}
        sx={{ mt: 2 }}
      >
        {t('common.btn.submit')}
      </LoadingButton>
    </Stack>
  )
}
